import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Anchor,
  Box,
  Group,
  Progress,
  Stack,
  Text,
  UnstyledButton,
} from '@cowbell/ui';
import {
  type ComponentProps,
  createContext,
  type Ref,
  useContext,
} from 'react';
import { Link } from 'react-router-dom';
import { rem } from '../../theme/rem';
import classes from './navigator.module.css';
import type {
  FlowProgress,
  FlowStep,
  FlowSubstep,
  SubstepState,
} from './progress';
import tile from './tile.module.css';

export type LinkTarget = ComponentProps<typeof Link>['to'];

export interface StepNavigatorProps<Target, SubTarget> {
  readonly progress: FlowProgress<Target, SubTarget>;
  /** Names the navigation landmark. */
  readonly label: string;
  /** The heading over the percentage. */
  readonly heading?: string;
  /** A line under the progress bar, such as how long the flow takes. */
  readonly footnote?: string;
  /** The link for a step the broker may open. */
  readonly linkTo: (target: Target) => LinkTarget;
  /** The link for a substep the broker may open. */
  readonly substepLinkTo: (target: SubTarget) => LinkTarget;
  /** The line under a step, or null for none. */
  readonly detail: (step: FlowStep<Target, SubTarget>) => string | null;
  /** Asked to open a page of a step, by its index, when the broker chooses a substep that names one. */
  readonly onPage?: (page: number, anchor?: string) => void;
  /** Told when the broker chooses a step, a screen or a page: a drawer holding the navigator closes then. */
  readonly onNavigate?: () => void;
  /** Where the navigator's content is, which the shell measures beside the step to snap the two to one height. */
  readonly contentRef?: Ref<HTMLDivElement>;
  /** The height the shell snaps it to beside the step, in rem: the step's tile's, when that is only a little taller. */
  readonly minHeight?: string;
}

/** What choosing anything in the navigator does besides going there. */
const NavigateContext = createContext<() => void>(() => undefined);

const FILLED = 'var(--mantine-primary-color-filled)';
const IDLE = 'var(--mantine-color-default-border)';

interface StepCircleProps {
  readonly step: Pick<FlowStep<unknown>, 'number' | 'state'>;
}

/** The step's number in a circle, or a check once it is done. */
function StepCircle({ step }: StepCircleProps) {
  const filled = step.state !== 'upcoming';
  return (
    <Box
      aria-hidden
      display="flex"
      style={{
        width: rem(32),
        height: rem(32),
        flexShrink: 0,
        borderRadius: '50%',
        border: `${rem(2)} solid ${filled ? FILLED : IDLE}`,
        background: filled ? FILLED : 'transparent',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {step.state === 'done' ? (
        <FontAwesomeIcon
          icon={faCheck}
          style={{
            width: rem(14),
            height: rem(14),
            color: 'var(--mantine-color-white)',
          }}
        />
      ) : (
        <Text
          component="span"
          size="sm"
          fw={700}
          lh={1}
          c={filled ? 'white' : 'dimmed'}
        >
          {step.number}
        </Text>
      )}
    </Box>
  );
}

const SUBSTEP_DOT: Readonly<Record<SubstepState, string>> = {
  done: FILLED,
  current: FILLED,
  unfinished: IDLE,
  upcoming: IDLE,
};

interface SubstepLabelProps<SubTarget> {
  readonly substep: FlowSubstep<SubTarget>;
  readonly linkTo: (target: SubTarget) => LinkTarget;
  readonly onPage?: (page: number, anchor?: string) => void;
}

/**
 * A substep's name: a link to it once passed, and text before then; a page of step 7 the broker has reached asks the
 * step to open it. A page is named by a section title as the server sends it, which can run long: it is cut to two
 * lines on screen, and read whole by its accessible name and its tooltip.
 */
function SubstepLabel<SubTarget>({
  substep,
  linkTo,
  onPage,
}: SubstepLabelProps<SubTarget>) {
  const { opens, page } = substep;
  const navigated = useContext(NavigateContext);
  if (page !== undefined) {
    return (
      <Anchor
        component="button"
        type="button"
        size="sm"
        ta="left"
        title={substep.label}
        className={classes.page}
        data-current={substep.state === 'current' || undefined}
        onClick={() => {
          onPage?.(page, substep.anchor);
          navigated();
        }}
      >
        <span className={classes.clamp}>{substep.label}</span>
      </Anchor>
    );
  }
  if (opens === null) {
    return (
      <Text
        size="sm"
        fw={substep.state === 'current' ? 600 : 400}
        c={substep.state === 'upcoming' ? 'dimmed' : undefined}
        title={substep.label}
        className={classes.clamp}
      >
        {substep.label}
      </Text>
    );
  }
  return (
    <Anchor component={Link} to={linkTo(opens)} size="sm" onClick={navigated}>
      {substep.label}
    </Anchor>
  );
}

interface SubstepsProps<SubTarget> {
  /** The step they belong to, which names the list. */
  readonly step: string;
  readonly substeps: readonly FlowSubstep<SubTarget>[];
  readonly linkTo: (target: SubTarget) => LinkTarget;
  readonly onPage?: (page: number, anchor?: string) => void;
}

function Substeps<SubTarget>({
  step,
  substeps,
  linkTo,
  onPage,
}: SubstepsProps<SubTarget>) {
  return (
    <Stack
      component="ol"
      aria-label={`${step} steps`}
      gap={rem(6)}
      mt="sm"
      ml={rem(2)}
      p={0}
      style={{ listStyle: 'none' }}
    >
      {/* Two pages can share a name, as two sections can share a title. */}
      {substeps.map((substep, at) => (
        <Group
          key={`${at}:${substep.label}`}
          component="li"
          gap="xs"
          wrap="nowrap"
          // The dot sits at the middle of the first line, as a bullet does, when a name wraps to a second.
          align="flex-start"
          aria-current={substep.state === 'current' ? 'step' : undefined}
        >
          <Box
            aria-hidden
            style={{
              width: rem(8),
              height: rem(8),
              marginTop: `calc((var(--mantine-font-size-sm) * var(--mantine-line-height-sm, 1.4) - ${rem(
                8
              )}) / 2)`,
              flexShrink: 0,
              borderRadius: '50%',
              background: SUBSTEP_DOT[substep.state],
            }}
          />
          <SubstepLabel substep={substep} linkTo={linkTo} onPage={onPage} />
        </Group>
      ))}
    </Stack>
  );
}

interface StepRowProps<Target, SubTarget> {
  readonly step: FlowStep<Target, SubTarget>;
  readonly detail: string | null;
  readonly linkTo: (target: Target) => LinkTarget;
  readonly substepLinkTo: (target: SubTarget) => LinkTarget;
  readonly onPage?: (page: number, anchor?: string) => void;
}

function StepRow<Target, SubTarget>({
  step,
  detail,
  linkTo,
  substepLinkTo,
  onPage,
}: StepRowProps<Target, SubTarget>) {
  const navigated = useContext(NavigateContext);
  const content = (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      <StepCircle step={step} />
      <Box pt={rem(3)}>
        <Text
          className={classes.stepLabel}
          size="md"
          fw={step.state === 'upcoming' ? 400 : 600}
          c={step.state === 'upcoming' ? 'dimmed' : undefined}
        >
          {step.label}
        </Text>
        {detail && (
          <Text size="sm" c="dimmed">
            {detail}
          </Text>
        )}
        {step.substeps.length > 0 && (
          <Substeps
            step={step.label}
            substeps={step.substeps}
            linkTo={substepLinkTo}
            onPage={onPage}
          />
        )}
      </Box>
    </Group>
  );

  return (
    <Box
      component="li"
      aria-current={step.state === 'current' ? 'step' : undefined}
    >
      {step.opens === null ? (
        content
      ) : (
        <UnstyledButton
          className={classes.step}
          component={Link}
          to={linkTo(step.opens)}
          onClick={navigated}
        >
          {content}
        </UnstyledButton>
      )}
    </Box>
  );
}

/**
 * The broker's progress through a flow, step by step, with the current step's substeps under it. A step the broker
 * may go back or on to is a link, and so is each substep already passed.
 */
export function StepNavigator<Target, SubTarget>({
  progress,
  label,
  heading = 'Your progress',
  footnote,
  linkTo,
  substepLinkTo,
  detail,
  onPage,
  onNavigate = () => undefined,
  contentRef,
  minHeight,
}: StepNavigatorProps<Target, SubTarget>) {
  return (
    <NavigateContext.Provider value={onNavigate}>
      <Box
        component="nav"
        aria-label={label}
        className={`${tile.tile} ${tile.snaps}`}
        style={{ minHeight: minHeight ?? 0 }}
      >
        {/* Its content, which the snap measures: the panel grows past it, this doesn't. */}
        <Box ref={contentRef} className={tile.measured} p="xl">
          <Group justify="space-between" mb="sm">
            <Text
              size="xs"
              fw={600}
              tt="uppercase"
              c="var(--mantine-primary-color-light-color)"
              className={classes.heading}
            >
              {heading}
            </Text>
            <Text size="xs" c="dimmed">
              {progress.percent}%
            </Text>
          </Group>
          <Progress
            value={progress.percent}
            size="sm"
            radius="xl"
            mb={footnote === undefined ? 'xl' : 'xs'}
          />
          {footnote !== undefined && (
            <Text size="xs" c="dimmed" mb="xl">
              {footnote}
            </Text>
          )}
          <Stack
            component="ol"
            gap="xl"
            m={0}
            p={0}
            style={{ listStyle: 'none' }}
          >
            {progress.steps.map((step) => (
              <StepRow
                key={step.number}
                step={step}
                detail={detail(step)}
                linkTo={linkTo}
                substepLinkTo={substepLinkTo}
                onPage={onPage}
              />
            ))}
          </Stack>
        </Box>
      </Box>
    </NavigateContext.Provider>
  );
}
